import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useVenue } from "../VenueProvider";
import { useAuth } from "../AuthProvider";
import { esSoloLectura } from "../../utils/roles";
import { Badge } from "../badge";
import { Heading } from "../heading";
import ClasificacionWizard from "./ClasificacionWizard";
import ServicioCreateDialog from "./ServicioCreateDialog";
import {
  proveedorDe,
  esItemizado,
  estaClasificado,
  pendientesDeClasificar,
  esCobrable,
  esDescuento,
  esRenta,
  incluyeDe,
  incluidosSinRenta,
  rentasQueIncluyen,
  unidadDe,
  paquetesDe,
  variantesDe,
  precioDe,
  costoProveedorDe,
  tieneCosto,
} from "../../utils/catalogo";
import {
  RectangleStackIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

const fmtMoney = (v) =>
  "$" + (Number(v) || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 });

const FILTROS = [
  { value: "", label: "Todos" },
  { value: "renta", label: "Rentas" },
  { value: "venue", label: "Del venue" },
  { value: "proveedor", label: "De proveedor" },
  { value: "piezas", label: "Con piezas" },
  { value: "pendiente", label: "Sin revisar" },
];

// El catálogo de servicios del venue (FRA-347): todo lo que puede entrar a
// una cotización, con lo que cuesta. Cada renglón declara quién lo pone —el
// venue o un proveedor— y, si es del venue, si tiene piezas que administrar.
const ServiciosPage = () => {
  const navigate = useNavigate();
  const { conceptos, conceptosLoading, proveedores, selectedVenue } = useVenue();
  // Inversionista (P-FRA-13): ve el catálogo completo, sin alta, revisión ni
  // acomodo de incluidos — los avisos y sus asistentes son escritura.
  const { userInfo } = useAuth();
  const soloLectura = esSoloLectura(userInfo);

  const [search, setSearch] = useState("");
  const [filtro, setFiltro] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const proveedoresPorId = useMemo(() => {
    const map = {};
    (proveedores || []).forEach((p) => { map[p.id] = p; });
    return map;
  }, [proveedores]);

  const pendientes = useMemo(() => pendientesDeClasificar(conceptos), [conceptos]);
  // Marcados "incluido en la renta" pero sin renta que los traiga: existen y
  // no aparecen en ninguna cotización. El asistente los acomoda.
  const sueltos = useMemo(() => incluidosSinRenta(conceptos), [conceptos]);

  const visibles = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (conceptos || [])
      .filter((c) => !c.archivedAt)
      .filter((c) => {
        switch (filtro) {
          case "renta":
            return esRenta(c);
          case "venue":
            return !proveedorDe(c) && !esDescuento(c) && !esRenta(c);
          case "proveedor":
            return !!proveedorDe(c);
          case "piezas":
            return esItemizado(c);
          case "pendiente":
            return !estaClasificado(c) && !esDescuento(c);
          default:
            return true;
        }
      })
      .filter((c) => (term ? (c.nombre || "").toLowerCase().includes(term) : true))
      .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
  }, [conceptos, search, filtro]);

  const inputClass =
    "w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]";

  // "Cobras": el precio por unidad.
  const cobroTexto = (c) => (esCobrable(c) && precioDe(c) > 0 ? precioDe(c) : null);

  // "Te cuesta": la tarifa del proveedor, por unidad de cobro.
  const costoTexto = (c) =>
    tieneCosto(c)
      ? { monto: costoProveedorDe(c), sufijo: `/${unidadDe(c)}`, title: undefined }
      : null;

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <RectangleStackIcon className="h-6 w-6" style={{ color: ACCENT }} />
          <Heading level={2}>Servicios</Heading>
          {selectedVenue?.name && (
            <span className="text-zinc-400 hidden sm:inline">– {selectedVenue.name}</span>
          )}
        </div>
        <p className="text-sm text-zinc-500 mt-1">
          Todo lo que puedes poner en una cotización, con lo que te cuesta: lo
          que hace el venue, lo que dan tus proveedores, tus piezas físicas y
          lo que consumen.
        </p>
      </div>

      {/* Incluidos sin renta: el aviso que arregla el legado de la casilla
          "está incluido en la renta" de cuando no había renta a la que
          pertenecer. Va después del de revisión porque se atiende después. */}
      {!soloLectura && pendientes.length === 0 && sueltos.length > 0 && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/20 px-4 py-3 flex flex-wrap items-center gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-amber-800 dark:text-amber-300">
              {sueltos.length}{" "}
              {sueltos.length === 1
                ? "servicio incluido no está en ninguna renta"
                : "servicios incluidos no están en ninguna renta"}
            </div>
            <div className="text-xs text-amber-700 dark:text-amber-400">
              Están marcados como incluidos en la renta, pero ninguna renta los
              trae: así no aparecen en ninguna cotización.
            </div>
          </div>
          <button
            type="button"
            onClick={() => setWizardOpen(true)}
            className="ml-auto px-3 py-1.5 rounded-lg text-xs font-semibold text-white shadow-sm"
            style={{ backgroundColor: ACCENT }}
          >
            Acomodarlos
          </button>
        </div>
      )}

      {/* Aviso de revisión: no bloquea nada, solo empuja. */}
      {!soloLectura && pendientes.length > 0 && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/20 px-4 py-3 flex flex-wrap items-center gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-amber-800 dark:text-amber-300">
              {pendientes.length}{" "}
              {pendientes.length === 1 ? "servicio sin revisar" : "servicios sin revisar"}
            </div>
            <div className="text-xs text-amber-700 dark:text-amber-400">
              Siguen funcionando en tus cotizaciones igual que siempre. Revisarlos
              es decir quién los pone, y con eso el panel sabe lo que te cuestan.
            </div>
          </div>
          <button
            type="button"
            onClick={() => setWizardOpen(true)}
            className="ml-auto shrink-0 px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
            style={{ backgroundColor: ACCENT }}
          >
            Revisarlos
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[12rem]">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar…"
            className={`${inputClass} pl-9`}
          />
        </div>
        <select
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          className="rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm"
        >
          {FILTROS.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
        {!soloLectura && (
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
          style={{ backgroundColor: ACCENT }}
        >
          <PlusIcon className="h-4 w-4" />
          Nuevo servicio
        </button>
        )}
      </div>

      {conceptosLoading ? (
        <div className="text-center py-12 text-zinc-400">Cargando...</div>
      ) : visibles.length === 0 ? (
        <div className="text-center py-16">
          <RectangleStackIcon className="h-8 w-8 mx-auto text-zinc-300" />
          <div className="mt-2 text-zinc-900 dark:text-white font-semibold">
            Nada por aquí todavía
          </div>
          <p className="text-sm text-zinc-500 mt-1">
            Da de alta lo que vendes para poder cotizarlo y saber cuánto te deja.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-zinc-200 dark:border-zinc-700/60">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-50 dark:bg-zinc-800/40">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Servicio</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Quién lo da</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Cobras</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Te cuesta</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Notas</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
              {visibles.map((c) => {
                const pid = proveedorDe(c);
                const prov = pid ? proveedoresPorId[pid] : null;
                const cobro = cobroTexto(c);
                const costo = costoTexto(c);
                const paquetes = paquetesDe(c);
                const variantes = variantesDe(c);
                const revisar = !estaClasificado(c) && !esDescuento(c);
                return (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/dashboard/servicios/${c.id}`)}
                    className="cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition-colors"
                  >
                    <td className="px-4 py-2.5 font-medium text-zinc-900 dark:text-white">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {c.nombre}
                        {esDescuento(c) && <Badge color="red">Descuento</Badge>}
                        {esRenta(c) && <Badge color="amber">Renta</Badge>}
                        {esRenta(c) && incluyeDe(c).length > 0 && (
                          <Badge color="zinc">
                            Incluye {incluyeDe(c).length}
                          </Badge>
                        )}
                        {/* Antes decía "Incluido en la renta" a secas y no había
                            forma de saber en cuál: ahora se nombra la renta —o
                            se avisa que no está en ninguna, que es el caso que
                            hay que arreglar. */}
                        {!esRenta(c) && !esCobrable(c) && (() => {
                          const padres = rentasQueIncluyen(conceptos, c.id);
                          return padres.length === 0 ? (
                            <Badge color="amber">Incluido, sin renta</Badge>
                          ) : (
                            <Badge color="zinc">
                              {padres.length === 1
                                ? `Incluido en ${padres[0].nombre}`
                                : `Incluido en ${padres.length} rentas`}
                            </Badge>
                          );
                        })()}
                        {esItemizado(c) && <Badge color="emerald">Piezas</Badge>}
                        {variantes.length > 0 && (
                          <Badge color="indigo">
                            {variantes.length} {variantes.length === 1 ? "variante" : "variantes"}
                          </Badge>
                        )}
                        {paquetes.length > 0 && (
                          <Badge color="purple">
                            {paquetes.length} {paquetes.length === 1 ? "paquete" : "paquetes"}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      {esDescuento(c) ? (
                        <span className="text-xs text-zinc-300">—</span>
                      ) : pid ? (
                        <div className="flex items-center gap-1.5">
                          <Badge color="blue">Proveedor</Badge>
                          <span className="text-xs text-zinc-500 truncate">
                            {prov?.nombre || c.proveedorNombre || "—"}
                          </span>
                        </div>
                      ) : revisar && soloLectura ? (
                        <span className="text-xs text-amber-600">Sin revisar</span>
                      ) : revisar ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setWizardOpen(true);
                          }}
                          className="text-xs font-medium text-amber-600 hover:underline"
                        >
                          Sin revisar
                        </button>
                      ) : (
                        <Badge color="zinc">Del venue</Badge>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      {cobro ? (
                        <>
                          <span className="font-medium text-zinc-900 dark:text-white">
                            {fmtMoney(cobro)}
                          </span>
                          <span className="text-xs text-zinc-400"> /{unidadDe(c)}</span>
                        </>
                      ) : (
                        <span className="text-zinc-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap" title={costo?.title}>
                      {costo ? (
                        <>
                          <span className="font-medium text-red-600">{fmtMoney(costo.monto)}</span>
                          {costo.sufijo && <span className="text-xs text-zinc-400"> {costo.sufijo}</span>}
                        </>
                      ) : (
                        <span className="text-zinc-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-zinc-500 text-xs">
                      {c.descripcion || <span className="text-zinc-300">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!soloLectura && (
        <>
          <ClasificacionWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
          <ServicioCreateDialog
            open={createOpen}
            onClose={() => setCreateOpen(false)}
            onCreated={(id) => navigate(`/dashboard/servicios/${id}`)}
          />
        </>
      )}
    </div>
  );
};

export default ServiciosPage;
